import { create } from 'zustand';
import { isLive } from 'modules/CbamCertificates/utils/hedgeMath';

/**
 * The hedge simulation being built (cbam-hedging-scenarios HG2, HG6): a list
 * of simulated EUA hedges. Nothing here is traded or held (I2); every edit
 * lands here until its planning scenario is saved (cbam-scenario-builder SB1).
 * Edited in the scenario builder on either page, read by both; module state, so
 * it survives navigation. Whether the builder is open is not kept here (SB6).
 *
 *   legs  `{ id, instrument, quantity, lockedPrice, tradeDate, closeDate,
 *            collateralPct, floating }`
 *
 * `loaded` is the saved simulation this came from (or null), kept as saved so
 * "modified" is a comparison. Modelled on `useCertificateSimulationStore`.
 */

let seq = 0;
const newLegId = () => `hedge-${Date.now().toString(36)}-${(seq += 1)}`;

const fromSaved = (simulation) => ({ legs: (simulation?.hedges ?? []).map((h) => ({ ...h })) });

export const useHedgeSimulationStore = create((set) => ({
  legs: [],
  loaded: null,
  addLeg: (leg) => set((s) => ({ legs: [...s.legs, { id: newLegId(), ...leg }] })),
  patchLeg: (id, patch) =>
    set((s) => ({ legs: s.legs.map((l) => (l.id === id ? { ...l, ...patch } : l)) })),
  removeLeg: (id) => set((s) => ({ legs: s.legs.filter((l) => l.id !== id) })),
  clearLegs: () => set({ legs: [] }),
  /** Start a new, unsaved hedge simulation. */
  reset: () => set({ legs: [], loaded: null }),
  /** Open a saved hedge simulation. */
  load: (simulation) => set({ ...fromSaved(simulation), loaded: simulation ?? null }),
  /** After a save, the saved copy is the new reference point. */
  markSaved: (simulation) => set({ ...fromSaved(simulation), loaded: simulation }),
}));

const num = (v) => (v == null || v === '' ? null : Number(v));

/** The legs as the `/hedge-simulations` payload stores them. */
export const hedgePayload = (state) => ({
  hedges: state.legs.map((l) => ({
    id: l.id,
    instrument: l.instrument,
    quantity: num(l.quantity),
    lockedPrice: num(l.lockedPrice),
    tradeDate: l.tradeDate,
    closeDate: l.closeDate,
    collateralPct: l.instrument === 'forward' ? num(l.collateralPct) : null,
    floating: l.instrument === 'swap' ? (l.floating ?? null) : null,
  })),
});

const comparable = (s) => JSON.stringify(hedgePayload(s).hedges);

/** Active once a leg counts: a blanked draft is not a hedge. */
export const isHedgeSimulationActive = (state) => state.legs.some(isLive);

/** Whether the working legs differ from the saved ones (or exist at all, if new). */
export const isHedgeSimulationModified = (state) =>
  state.loaded
    ? comparable(state) !== comparable(fromSaved(state.loaded))
    : isHedgeSimulationActive(state);

export default useHedgeSimulationStore;
